import React, { useMemo } from 'react';
import {
  Box,
  EmptyListIcon,
  FiservCardInterface,
  FiservCardStatusEnum,
  Text,
  useResponsive,
} from '@common-fe/common-fe';
import styled from 'styled-components';

import { CardsMap } from '@/modules/transaction/components/Cards/Cards.types.ts';
import theme from '@/styles/theme';

import { CardsSectionWrapper } from './CardSectionWrapper';

const EmptyListWrap = styled(Box)`
  padding: 41px 0;
`;

interface Props {
  cardsByStatus: CardsMap[string];
  holderName: string;
  isLoading: boolean;
}

export const CardsSection: React.FC<Props> = ({ cardsByStatus, holderName, isLoading }) => {
  const sortedCards = useMemo(
    () =>
      Object.keys(cardsByStatus)
        .sort()
        .filter(
          (status) =>
            status !== FiservCardStatusEnum.CLOSED &&
            status !== FiservCardStatusEnum.PENDING_CLOSED &&
            status !== FiservCardStatusEnum.PENDING_REPLACE &&
            status !== FiservCardStatusEnum.STOLEN &&
            status !== FiservCardStatusEnum.LOST &&
            status !== FiservCardStatusEnum.NOT_SENT
        ),
    [cardsByStatus]
  );

  const closedCardByStatus = useMemo(
    () =>
      Object.keys(cardsByStatus)
        .sort()
        .filter(
          (status) =>
            status !== FiservCardStatusEnum.ACTIVE &&
            status !== FiservCardStatusEnum.FROZEN &&
            status !== FiservCardStatusEnum.PENDING_FROZEN &&
            status !== FiservCardStatusEnum.PENDING_UNLOCK &&
            status !== FiservCardStatusEnum.PENDING_OPEN
        ),
    [cardsByStatus]
  );

  const closedCards = useMemo(
    () =>
      closedCardByStatus.reduce(
        (prevValue, currentStatus) => [...prevValue, ...cardsByStatus[currentStatus as FiservCardStatusEnum]],
        [] as FiservCardInterface[]
      ),
    [cardsByStatus, closedCardByStatus]
  );
  const { isMobile } = useResponsive();
  const noCards = useMemo(() => !Object.keys(cardsByStatus).length, [cardsByStatus]);
  return (
    <Box
      direction="column"
      pad={{ bottom: 'spacing12' }}
      margin={{ bottom: 'spacing16' }}
      data-testid="Card-section-wrapper"
    >
      {!noCards ? (
        <>
          <Box
            direction="row"
            justify="between"
            margin={{ bottom: 'spacing12', horizontal: isMobile ? 'spacing16' : '' }}
          >
            <Text size="xxlarge" weight="bold">
              {holderName.split('-')[0]}
            </Text>
          </Box>
          <Box background="module" round="moduleRound" pad={isMobile ? 'spacing8' : 'spacing16'}>
            {sortedCards.map((status) => (
              <CardsSectionWrapper
                key={status as React.Key}
                status={status as FiservCardStatusEnum}
                cardList={cardsByStatus[status as FiservCardStatusEnum] as FiservCardInterface[]}
                isLoading={isLoading}
              />
            ))}

            {!!(closedCards || []).length && (
              <CardsSectionWrapper
                expandable
                status={FiservCardStatusEnum.CLOSED}
                cardList={closedCards}
                isLoading={isLoading}
              />
            )}
          </Box>
        </>
      ) : (
        <>
          <Box direction="row" justify="between" margin={{ bottom: theme.spacings.spacing24 }}>
            <Text size="xxlarge" weight="bold">
              {holderName}
            </Text>
          </Box>
          <Box background="module" round="moduleRound" pad="spacing16" margin={{ bottom: 'spacing24' }}>
            <EmptyListWrap justify="center" align="center" direction="row">
              <Box align="center">
                <EmptyListIcon />

                <Text
                  weight={500}
                  margin={{ top: 'spacing24', bottom: 'spacing12' }}
                  color="textBody"
                  size="xxlarge"
                >
                  There are no cards yet
                </Text>
                <Box direction="row" justify="center" margin={{ bottom: 'spacing24' }}>
                  <Text color="textBody" margin={{ right: 'spacing8' }}>
                    To add a card, click the blue-button above.
                  </Text>
                </Box>
              </Box>
            </EmptyListWrap>
          </Box>
        </>
      )}
    </Box>
  );
};
